// The in-room "Gravar" control (spec v3.8), visible to every participant.
// Four states: idle ("Gravar"), pending (disabled while the request is in
// flight), recording ("Gravando" with the red dot, disabled: no stop in v1)
// and unavailable (disabled, the pt-BR reason as label and tooltip).
import type { LiveRecordingStatus } from '@kima-hub/contracts';
import { COPY } from '../copy.js';

export type RecordControlState = 'idle' | 'pending' | 'recording' | 'unavailable';

export function recordControlState(status: LiveRecordingStatus, pending: boolean): RecordControlState {
  if (status.mode === 'recorded') return 'recording';
  if (pending) return 'pending';
  return status.can_start ? 'idle' : 'unavailable';
}

/** Why "Gravar" is disabled; not_started with can_start false means another gate is closed. */
function unavailableText(status: LiveRecordingStatus): string {
  const reason = status.unavailable_reason;
  if (!reason || reason === 'not_started') return COPY.recordUnavailable;
  return COPY.unavailableReason[reason];
}

export function RecordButton(props: {
  status: LiveRecordingStatus;
  pending: boolean;
  error: string | null;
  onPress: () => void;
}) {
  const state = recordControlState(props.status, props.pending);
  const label =
    state === 'recording'
      ? COPY.recording
      : state === 'pending'
        ? COPY.recordStarting
        : COPY.record;
  const hint = state === 'unavailable' ? unavailableText(props.status) : undefined;
  return (
    <div className="record-control">
      <button
        type="button"
        className={`button record-button record-button--${state}`}
        data-testid="record-button"
        data-state={state}
        disabled={state !== 'idle'}
        aria-label={hint ? `${label}: ${hint}` : label}
        aria-busy={state === 'pending' || undefined}
        title={hint}
        onClick={props.onPress}
      >
        <span className="record-button__dot" aria-hidden="true" />
        {label}
      </button>
      {props.error && state !== 'recording' && (
        <span className="record-control__error small" role="alert">
          {props.error}
        </span>
      )}
    </div>
  );
}
